import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import {
  CustomTooltipWrapper,
  CustomTooltip,
  TooltipIcon,
  TooltipLabel,
  TooltipSubHeading
} from "./styled";
import { CopyStrokeRegularIcon } from "assets/SVGComponents";

const CommonTooltip = ({
  children,
  subHeading,
  icon,
  iconType = false,
  tooltipContent,
  theme = "light",
  className = "",
  WrapperClass
}) => {
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate tooltip position
  useLayoutEffect(() => {
    if (visible && wrapperRef.current && tooltipRef.current) {
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      const OFFSET_Y = 1;
      const OFFSET_X = 8;

      let top =
        window.scrollY + wrapperRect.top - tooltipRect.height + OFFSET_Y;
      let left =
        window.scrollX +
        wrapperRect.left +
        wrapperRect.width / 2 -
        tooltipRect.width / 2;

      // Check right overflow
      if (
        left + tooltipRect.width >
        window.scrollX + window.innerWidth - OFFSET_X
      ) {
        left =
          window.scrollX + window.innerWidth - tooltipRect.width - OFFSET_X;
      }

      // Check left overflow
      if (left < window.scrollX + OFFSET_X) {
        left = window.scrollX + OFFSET_X;
      }

      // Optional: flip vertically if top goes above viewport
      if (top < window.scrollY + OFFSET_Y) {
        top = window.scrollY + wrapperRect.bottom + OFFSET_Y; // show below
      }

      setPosition({ top, left });
    }
  }, [visible]);

  // Hide on scroll
  useEffect(() => {
    const handleScroll = () => visible && setVisible(false);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [visible]);

  // Global hide
  useEffect(() => {
    const handleGlobalTooltipHide = () => setVisible(false);
    window.addEventListener("closeAllTooltips", handleGlobalTooltipHide);
    return () =>
      window.removeEventListener("closeAllTooltips", handleGlobalTooltipHide);
  }, []);

  const handleMouseEnter = () => {
    window.dispatchEvent(new Event("closeAllTooltips"));
    setVisible(true);
  };

  const handleMouseLeave = () => setVisible(false);

  return (
    <CustomTooltipWrapper
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={WrapperClass}
    >
      {children}

      {visible &&
        ReactDOM.createPortal(
          <CustomTooltip
            ref={tooltipRef}
            className={`custom-tooltip visible ${className}`}
            $theme={theme}
            style={{
              position: "absolute",
              top: `${position.top}px`,
              left: `${position.left}px`,
              zIndex: 9999
            }}
          >
            {/* Optional Icon */}
            {iconType && (
              <TooltipIcon>
                <CopyStrokeRegularIcon
                  color={theme === "dark" ? "#ffffff" : "#1F1F1F"}
                />
              </TooltipIcon>
            )}

            <div>
              {/* Custom icon + subheading */}
              {icon && (
                <TooltipIcon>
                  {icon}
                  {subHeading && (
                    <TooltipSubHeading $theme={theme}>
                      {subHeading}
                    </TooltipSubHeading>
                  )}
                </TooltipIcon>
              )}

              {/* Tooltip content */}
              {tooltipContent && (
                <TooltipLabel $theme={theme}>{tooltipContent}</TooltipLabel>
              )}
            </div>
          </CustomTooltip>,
          document.body
        )}
    </CustomTooltipWrapper>
  );
};

export default CommonTooltip;
